/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { SwapOutlined, PlusOutlined } from '@ant-design/icons'
import { Select, InputRef } from 'antd'
import React, { useCallback, useEffect, useRef } from 'react'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'
import { paragraphSmall } from 'src/components/css'
import {
  getTeamDropdownOptions,
  CREATE_NEW_TEAM_VALUE,
} from '../utils/teamDropdown'

// Define types for better type safety
interface Team {
  id?: string
  uid?: string
  team_player_roster_id?: string
  name: string
  is_bucket?: boolean
  player_count?: number
  max_players_per_team?: number
}

export interface TeamInfoSectionProps {
  isTeamRep: boolean
  userTeams: Team[] | null | undefined
  showExistingRegistrationsPlayers: boolean
  selectedTeam: Team | null
  setSelectedTeam: (team: Team | null) => void
  isAddingNewTeam: boolean
  setIsAddingNewTeam: (value: boolean) => void
  newTeamName: string
  setNewTeamName: (name: string) => void
  fieldErrors: string[]
  customError: Record<string, string>
  isCanlan: boolean
}

// Static styles to avoid re-creation on every render
const containerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  margin-top: 16px;
`

const headerContainerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  align-self: stretch;
`

const titleContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`

const getSwitchButtonStyle = (_isCanlanValue: boolean) => css`
  color: ${PRIMARY_BUTTON_CONTENT.text};
  background-color: ${colors.PRIMARY};
  ${paragraphSmall}
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: flex-end;
`

const getHiddenSwitchButtonStyle = (isCanlanValue: boolean) => css`
  ${getSwitchButtonStyle(isCanlanValue)}
  display: none;
  cursor: default;
  pointer-events: none;
`

const formContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`

const newTeamFormStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`

const newTeamRowStyle = css`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
`

const newTeamFieldStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
`

const selectStyle = css`
  width: 100%;
  height: 50px;
  .ant-select-selection-item {
    display: flex;
    align-items: center;
  }
`

const selectWrapperStyle = css`
  width: 50%;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const errorStyle = css`
  transition: opacity 0.1s ease-in-out;
  font-size: 13px;
  line-height: 16px;
  color: ${colors.ERROR_LIGHT};
  align-self: stretch;
  margin-top: 8px;
`

/**
 * 🔄 TeamInfoSection: Reusable component for team selection and creation
 * Handles both team selection from existing teams and new team creation
 */
const TeamInfoSection: React.FC<TeamInfoSectionProps> = ({
  isTeamRep,
  userTeams,
  showExistingRegistrationsPlayers,
  selectedTeam,
  setSelectedTeam,
  isAddingNewTeam,
  setIsAddingNewTeam,
  newTeamName,
  setNewTeamName,
  fieldErrors,
  customError,
  isCanlan: isCanlanProp,
}) => {
  console.log('🔄 TeamInfoSection: Rendering team info section', {
    isTeamRep,
    userTeamsCount: userTeams?.length || 0,
    isAddingNewTeam,
    selectedTeamId: selectedTeam?.id || selectedTeam?.uid,
  })

  // Ref to control focus on the new team name input
  const newTeamInputRef = useRef<InputRef | null>(null)

  // When switching to "Create New Team" mode, focus the team name input
  useEffect(() => {
    if (isAddingNewTeam) {
      // Defer to next tick to ensure the input is mounted
      const tid = setTimeout(() => {
        try {
          newTeamInputRef.current?.focus?.()
        } catch (e) {
          // no-op
        }
      }, 0)
      return () => clearTimeout(tid)
    }
  }, [isAddingNewTeam])

  /**
   * 🔄 Auto-select when only a single selectable team exists
   * - Triggers when there is exactly one enabled option in the dropdown
   * - Respects current mode and user choice (does not override if already selected or in new-team mode)
   */
  useEffect(() => {
    try {
      if (!userTeams || userTeams.length === 0) {
        console.log('🔄 TeamInfoSection: No teams found')
        return
      }
      if (selectedTeam) {
        console.log('🔄 TeamInfoSection: Selected team found')
        return
      }
      if (isAddingNewTeam) {
        console.log('🔄 TeamInfoSection: Adding new team')
        return
      }

      const groupedOptions = getTeamDropdownOptions(
        userTeams || [],
        isTeamRep,
        showExistingRegistrationsPlayers
      )

      // Flatten groups and filter out non-selectable entries
      const flatOptions: Array<{ value: string; disabled?: boolean }> = []
      groupedOptions.forEach((grp: any) => {
        if (Array.isArray(grp?.options)) {
          grp.options.forEach((opt: any) => flatOptions.push(opt))
        } else if (grp?.value) {
          flatOptions.push(grp)
        }
      })

      const selectable = flatOptions.filter(
        (opt) =>
          !!opt?.value &&
          opt.value !== CREATE_NEW_TEAM_VALUE &&
          opt.disabled !== true
      )

      if (selectable.length === 1) {
        const onlyValue = selectable[0].value
        const team = userTeams.find(
          (t: any) =>
            t?.team_player_roster_id === onlyValue ||
            t?.uid === onlyValue ||
            t?.id === onlyValue
        )
        if (team) {
          setSelectedTeam({ ...team })
        }
      }
    } catch (e) {
      console.error('❌ Auto-select team failed:', e)
    }
  }, [
    userTeams,
    isTeamRep,
    showExistingRegistrationsPlayers,
    selectedTeam,
    isAddingNewTeam,
    setSelectedTeam,
  ])

  /**
   * 🔄 handleTeamChange: Handles team selection change in dropdown
   */
  const handleTeamChange = useCallback(
    (value: string | null) => {
      console.log('🔄 handleTeamChange: Team selection changed', { value })

      if (!value) {
        // Handle clearing team selection - don't call setSelectedTeam with null
        console.log('🔄 handleTeamChange: Clearing team selection')
        return
      }

      if (value === CREATE_NEW_TEAM_VALUE) {
        setIsAddingNewTeam(true)
        setNewTeamName('')
        setSelectedTeam(null)
        // Handle creating new team - don't call setSelectedTeam with null
        console.log('🔄 handleTeamChange: Switching to new team creation')
        return
      }

      const selectedTeamData = userTeams?.find(
        (team) =>
          team.uid === value ||
          team.id === value ||
          team.team_player_roster_id === value
      )

      if (selectedTeamData) {
        setSelectedTeam({ ...selectedTeamData })
        setIsAddingNewTeam(false)
      }
    },
    [userTeams, setSelectedTeam, setIsAddingNewTeam, setNewTeamName]
  )

  /**
   * 🔄 handleFilterOption: Filters team options in dropdown search
   */
  const handleFilterOption = useCallback((input: string, option: any) => {
    if (!input || !option?.label?.props?.children) {
      return false
    }
    try {
      const teamName = option.label.props.children[0]?.props?.children || ''
      return teamName
        .toString()
        .toLowerCase()
        .includes(input?.toLowerCase() || '')
    } catch (e) {
      console.error('❌ Error filtering team options:', e)
      return false
    }
  }, [])

  /**
   * 🔄 handleSwitchToExisting: Switches from new team creation to existing team selection
   */
  const handleSwitchToExisting = useCallback(() => {
    console.log(
      '🔄 handleSwitchToExisting: Switching to existing team selection'
    )
    setIsAddingNewTeam(false)
    setNewTeamName('')
    // Don't call setSelectedTeam with null
  }, [])

  /**
   * 🔄 handleSwitchToNew: Switches from existing team selection to new team creation
   */
  const handleSwitchToNew = useCallback(() => {
    console.log('🔄 handleSwitchToNew: Switching to new team creation')
    // Don't call setSelectedTeam with null
    setIsAddingNewTeam(true)
    setSelectedTeam(null)
  }, [])

  const teamNameError = fieldErrors.includes('team_name')
  const teamIdError =
    fieldErrors.includes('team_id') ||
    fieldErrors.includes('team') ||
    fieldErrors.includes('selectedTeam')

  // Debug logging to help understand what errors are being received
  console.log('🔄 TeamInfoSection: Error state debug', {
    fieldErrors,
    customError,
    teamNameError,
    teamIdError,
  })

  return (
    <div css={containerStyle} id='team-info-section'>
      <div css={headerContainerStyle}>
        <div css={titleContainerStyle}>
          <Text variant='captionLarge' color={colors.WHITE} weight='semiBold'>
            {!isTeamRep
              ? 'Choose a Team or Draft'
              : isAddingNewTeam
              ? 'Create a New Team'
              : 'Choose One of Your Teams'}
          </Text>
          {isTeamRep ? (
            <Text
              variant='paragraphSmall'
              color={colors.NEUTRAL_50}
              weight='regular'
            >
              The dropdown below lists all teams you either own, or have Team
              Official permissions for.
              <br />
              Choose any of these teams to enroll them into this competition.
            </Text>
          ) : null}
        </div>

        {/* Switch to Existing Team Button */}
        {isAddingNewTeam && (userTeams?.length || 0) > 0 ? (
          <p
            onClick={handleSwitchToExisting}
            css={getSwitchButtonStyle(isCanlanProp)}
          >
            <SwapOutlined style={{ fontSize: 16 }} /> Select Existing Team
          </p>
        ) : null}

        {/* Switch to New Team Button (currently hidden) */}
        {!isAddingNewTeam && (userTeams?.length || 0) > 0 && isTeamRep ? (
          <p
            onClick={handleSwitchToNew}
            css={getHiddenSwitchButtonStyle(isCanlanProp)}
          >
            <PlusOutlined style={{ fontSize: 16 }} /> Create New Team
          </p>
        ) : null}
      </div>

      <div css={formContainerStyle}>
        {!isAddingNewTeam ? (
          <Text variant='paragraphSmall' color={colors.WHITE} weight='regular'>
            Team *
          </Text>
        ) : null}

        {isAddingNewTeam && isTeamRep ? (
          <div css={newTeamFormStyle}>
            <div css={newTeamRowStyle}>
              <div css={newTeamFieldStyle}>
                <div
                  id='field-team_name'
                  css={[
                    newTeamFieldStyle,
                    css`
                      width: 50%;
                      @media (max-width: 768px) {
                        width: 100%;
                      }
                    `,
                  ]}
                >
                  <TextInput
                    ref={newTeamInputRef}
                    name='team_name'
                    label='Team Name *'
                    value={newTeamName}
                    setValue={setNewTeamName}
                    fieldErrors={fieldErrors}
                    customError={customError}
                    placeholder='The Sport Ninjas'
                    autoComplete='off'
                    maxLength={255}
                    tabIndex={1}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              id='field-team_id'
              data-field-names='team_id,team,team_name,selectedTeam'
              css={selectWrapperStyle}
            >
              <Select
                css={selectStyle}
                status={teamIdError || teamNameError ? 'error' : undefined}
                placeholder='Select a Team'
                defaultValue={
                  selectedTeam?.team_player_roster_id ||
                  selectedTeam?.uid ||
                  selectedTeam?.id
                }
                value={
                  selectedTeam?.team_player_roster_id ||
                  selectedTeam?.uid ||
                  selectedTeam?.id
                }
                onChange={handleTeamChange}
                options={getTeamDropdownOptions(
                  userTeams || [],
                  isTeamRep,
                  showExistingRegistrationsPlayers
                )}
                allowClear
                onClear={() => {
                  console.log('🔄 TeamInfoSection: Clearing selected team')
                  // Don't call setSelectedTeam with null since context doesn't support it
                  // The parent component should handle this case
                }}
                showSearch
                filterOption={handleFilterOption}
                tabIndex={1}
              />
            </div>
            <div
              tabIndex={-1}
              css={css`
                ${errorStyle}
                opacity: ${teamNameError ? 1 : 0};
              `}
            >
              <span>{customError.team_name}</span>
            </div>
          </>
        )}
      </div>

      {/* Team ID Error Display */}
      <div
        tabIndex={-1}
        css={css`
          ${errorStyle}
          opacity: ${teamIdError ? 1 : 0};
          display: ${teamIdError ? 'block' : 'none'};
        `}
      >
        <span>{customError.team_id}</span>
      </div>
    </div>
  )
}

export default TeamInfoSection
